/**
 * Riders List Page
 * 
 * Displays riders in list/grid view with:
 * - List/grid view toggle
 * - Season filter
 * - Rider cards with stats
 * - Create/edit/delete buttons for admin/coach
 * 
 * Requirements: 4.1, 4.2, 4.3, 4.5
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useRidersStore from '../store/ridersStore';
import { useAuth } from '../auth/AuthProvider';
import { Rider, User, restClient } from '../api/rest-client';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { Modal } from '../components/common/Modal';
import { formatPhoneForDisplay } from '../utils/phone';
import { formatRiderName, getRiderTypeBadge } from '../utils/riderDisplay';
import { SkillsPanel } from '../components/features/skills/SkillsPanel';
import { useGroupLeaderStore } from '../store/groupLeaderStore';
import { useEventsStore } from '../store/eventsStore';
import RaceRegistrationStatus from '../components/features/riders/RaceRegistrationStatus';
import { filterRaceEvents } from '../utils/raceRegistration';
import { resolveAssociation, type AssociationEntry } from '../utils/associations';

/**
 * Parse associated users from JSON string
 */
function parseAssociatedUsers(associatedUsers?: string): AssociationEntry[] {
  if (!associatedUsers) return [];
  try {
    const parsed = JSON.parse(associatedUsers);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}
/**
 * Get available seasons from riders
 */
function getAvailableSeasons(riders: Rider[]): string[] {
  const seasons = new Set<string>();
  
  riders.forEach(rider => {
    // Add seasons from seasons_active array if available
    if (rider.seasons_active && rider.seasons_active.length > 0) {
      rider.seasons_active.forEach(season => {
        if (season) seasons.add(season); // Only add non-null/undefined seasons
      });
    } else if (rider.season) {
      // Fallback to season field if seasons_active is not available
      seasons.add(rider.season);
    }
  });
  
  return Array.from(seasons).sort((a, b) => {
    // Handle null/undefined values
    const aStr = String(a || '');
    const bStr = String(b || '');
    return bStr.localeCompare(aStr); // Descending order
  });
}

/**
 * Get rider display name with type badge
 */
function getRiderDisplayInfo(rider: Rider): { name: string; badge: string; badgeColor: string } {
  const badge = getRiderTypeBadge(rider);
  return {
    name: formatRiderName(rider),
    badge: badge.text,
    badgeColor: badge.color,
  };
}

/**
 * Rider Card Component
 */
interface RiderCardProps {
  rider: Rider;
  onView: (id: string) => void;
  onEdit?: (id: string) => void;
  onAssociateUser?: (rider: Rider) => void;
  onSkills?: (rider: Rider) => void;
  showActions: boolean;
  users: User[];
  raceEvents: Array<{ event_id: string; event_name: string; event_starttime: string }>;
  currentSeason: string;
}

function RiderCard({ rider, onView, onEdit, onAssociateUser, onSkills, showActions, users, raceEvents, currentSeason }: RiderCardProps) {
  const displayInfo = getRiderDisplayInfo(rider);
  const isGroupLeader = useGroupLeaderStore(s => s.isGroupLeader);
  // Parse associated users
  const associatedUsers = parseAssociatedUsers(rider.associatedUsers);
  
  // Get user details for each association — never render a raw email/UUID for
  // an unresolved (former) user. (FR-4)
  const associatedUserDetails = associatedUsers.map(assoc => resolveAssociation(assoc, users));

  // Flag (red card) a CURRENT-SEASON rider that has no associated user — mirrors
  // the Users page, which flags users with no current-season rider.
  const inCurrentSeason =
    !!currentSeason &&
    (rider.season === currentSeason ||
      (rider.seasons_active ? rider.seasons_active.includes(currentSeason) : false));
  const hasNoUser = associatedUsers.length === 0;
  const showRedBackground = inCurrentSeason && hasNoUser;

  return (
    <Card className={`hover:shadow-lg transition-shadow ${showRedBackground ? '!bg-red-50' : ''}`}>
      <div className="flex flex-col h-full">
        {/* Type Badge */}
        <div className="mb-3">
          <span className={`inline-block px-3 py-1 text-xs font-semibold text-white ${displayInfo.badgeColor} rounded-full`}>
            {displayInfo.badge}
          </span>
        </div>

        {/* Rider Name */}
        <h3 className="text-xl font-bold text-gray-900 mb-2">
          {formatRiderName(rider)}{isGroupLeader(rider.rider_id) ? ' ⭐' : ''}
        </h3>

        {/* Active Seasons */}
        {rider.seasons_active && rider.seasons_active.length > 0 && (
          <div className="mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-medium text-gray-600">Seasons:</span>
              {rider.seasons_active
                .filter(season => season) // Filter out null/undefined
                .sort((a, b) => {
                  const aStr = String(a || '');
                  const bStr = String(b || '');
                  return bStr.localeCompare(aStr);
                })
                .map(season => (
                  <span
                    key={season}
                    className="inline-block px-2 py-0.5 text-xs font-medium text-primary-700 bg-primary-100 rounded"
                  >
                    {season}
                  </span>
                ))}
            </div>
          </div>
        )}

        {/* Rider Details */}
        <div className="flex-grow space-y-2 text-sm text-gray-600 mb-4">
          {/* Category and Group */}
          <div className="flex items-start">
            <svg
              className="w-5 h-5 mr-2 text-gray-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <span>{rider.category} • {rider.group}</span>
          </div>

          {/* Grade (for riders) or Level (for coaches) */}
          {rider.grade && (
            <div className="flex items-start">
              <svg
                className="w-5 h-5 mr-2 text-gray-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                />
              </svg>
              <span>Grade: {rider.grade}</span>
            </div>
          )}

          {rider.level && (
            <div className="flex items-start">
              <svg
                className="w-5 h-5 mr-2 text-gray-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                />
              </svg>
              <span>Level: {rider.level}</span>
            </div>
          )}

          {rider.birthday && (
            <div className="flex items-start">
              <svg
                className="w-5 h-5 mr-2 text-gray-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0A1.75 1.75 0 003 15.546m18 0v2.704M3 15.546v2.704m0 0h18M12 3v2m-4 2l1.5 1.5M16 7l-1.5 1.5M12 7a3 3 0 100 6 3 3 0 000-6z"
                />
              </svg>
              <span>Birthday: {(() => {
                const [y, m, d] = rider.birthday!.split('-');
                return `${m}/${d}/${y}`;
              })()}</span>
            </div>
          )}

          {/* Contact Info */}
          {rider.email && (
            <div className="flex items-start">
              <svg
                className="w-5 h-5 mr-2 text-gray-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
              <span className="text-xs break-all">{rider.email}</span>
            </div>
          )}

          {rider.phone && (
            <div className="flex items-start">
              <svg
                className="w-5 h-5 mr-2 text-gray-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                />
              </svg>
              <span className="text-xs">{formatPhoneForDisplay(rider.phone)}</span>
            </div>
          )}
          
          {/* Associated Users */}
          {associatedUserDetails.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <div className="text-xs font-medium text-gray-700 mb-1">Associated Users:</div>
              <div className="space-y-1">
                {associatedUserDetails.map((assoc, index) => (
                  <div
                    key={index}
                    className="text-xs text-gray-600"
                    data-testid="associated-user"
                  >
                    <span className={assoc.resolved ? 'font-medium' : 'italic text-gray-400'}>
                      {assoc.label}
                    </span>
                    <span className="text-gray-500"> - {assoc.role}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Race Registration Status — Requirements: 12.1, 12.2, 12.3, 12.4, 12.5, 12.6 */}
          {rider.type !== 'Coach' && raceEvents.length > 0 && (
            <div className="mt-2 pt-2 border-t border-gray-200">
              <RaceRegistrationStatus
                raceRegistrations={rider.race_registrations ?? {}}
                raceEvents={raceEvents}
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 mt-auto">
          {showActions && (
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={() => onEdit?.(rider.rider_id)}
                className="flex-1 text-sm py-2"
              >
                Edit
              </Button>
              
              {onAssociateUser && (
                <Button
                  variant="secondary"
                  onClick={() => onAssociateUser(rider)}
                  className="flex-1 text-sm py-2"
                >
                  Associate User
                </Button>
              )}

              {onSkills && (
                <Button
                  variant="secondary"
                  onClick={() => onSkills(rider)}
                  className="flex-1 text-sm py-2"
                  aria-label={`View skills for ${formatRiderName(rider)}`}
                >
                  Skills
                </Button>
              )}
            </div>
          )}
          
          <Button
            variant="primary"
            onClick={() => onView(rider.rider_id)}
            className="w-full text-sm py-2"
          >
            View Details
          </Button>
        </div>
      </div>
    </Card>
  );
}

/**
 * Riders List Page Component
 */
export function Riders() {
  const navigate = useNavigate();
  
  // Get riders from store
  const {
    riders,
    selectedSeason,
    loading,
    error,
    fetchRiders,
    setSelectedSeason,
  } = useRidersStore();

  // Get auth state for role-based actions
  const { isAdmin, isCoach } = useAuth();
  const showActions = isAdmin || isCoach;

  // Get events from events store for race registration display
  const { events: allEvents, fetchEvents } = useEventsStore();

  // Local state
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [filterType, setFilterType] = useState<'all' | 'riders' | 'coaches'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // User association state
  const [users, setUsers] = useState<User[]>([]);
  const [isAssociateModalOpen, setIsAssociateModalOpen] = useState(false);
  const [selectedRider, setSelectedRider] = useState<Rider | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('Parent');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [associationError, setAssociationError] = useState<string | null>(null);

  // Skills panel state
  const [skillsRider, setSkillsRider] = useState<Rider | null>(null);

  // Current season (from season config) — used to flag current-season riders
  // that have no associated user.
  const [currentSeason, setCurrentSeason] = useState('');

  // Fetch riders on mount
  useEffect(() => {
    fetchRiders();
  }, [fetchRiders]);

  // Load the current season once on mount
  useEffect(() => {
    restClient
      .getSeasonConfig()
      .then(cfg => setCurrentSeason(cfg.currentSeason))
      .catch(() => {});
  }, []);

  // Fetch events for race registration display
  useEffect(() => {
    if (selectedSeason) {
      fetchEvents(selectedSeason);
    }
  }, [selectedSeason, fetchEvents]);

  // Compute race events for the current season
  const raceEvents = useMemo(() => {
    const seasonEvents = selectedSeason
      ? allEvents.filter(e => e.season === selectedSeason)
      : allEvents;
    return filterRaceEvents(seasonEvents);
  }, [allEvents, selectedSeason]);
  
  // Fetch users for association
  useEffect(() => {
    if (showActions) {
      restClient.getUsers().then(setUsers).catch(() => {});
    }
  }, [showActions]);

  // Get available seasons
  const availableSeasons = useMemo(() => getAvailableSeasons(riders), [riders]);

  // Filter riders by selected season
  const seasonFilteredRiders = useMemo(() => {
    if (!selectedSeason) {
      return riders;
    }
    // Check if the rider's seasons_active includes the selected season
    return riders.filter(rider => {
      if (rider.seasons_active && rider.seasons_active.length > 0) {
        return rider.seasons_active.includes(selectedSeason);
      }
      // Fallback to checking the season field if seasons_active is not available
      return rider.season === selectedSeason;
    });
  }, [riders, selectedSeason]);

  // Filter by type (riders/coaches)
  const typeFilteredRiders = useMemo(() => {
    if (filterType === 'all') {
      return seasonFilteredRiders;
    }
    if (filterType === 'riders') {
      return seasonFilteredRiders.filter(r => r.type !== 'Coach');
    }
    return seasonFilteredRiders.filter(r => r.type === 'Coach');
  }, [seasonFilteredRiders, filterType]);

  // Filter by search query
  const searchFilteredRiders = useMemo(() => {
    if (!searchQuery.trim()) {
      return typeFilteredRiders;
    }

    const query = searchQuery.toLowerCase();
    return typeFilteredRiders.filter(rider => {
      // Search by rider fields
      return (
        rider.name?.toLowerCase().includes(query) ||
        rider.email?.toLowerCase().includes(query) ||
        rider.phone?.toLowerCase().includes(query) ||
        rider.category?.toLowerCase().includes(query) ||
        rider.group?.toLowerCase().includes(query) ||
        rider.grade?.toLowerCase().includes(query) ||
        rider.level?.toLowerCase().includes(query)
      );
    });
  }, [typeFilteredRiders, searchQuery]);

  // Sort riders by name
  const sortedRiders = useMemo(() => {
    return [...searchFilteredRiders].sort((a, b) => {
      const aName = String(a.name || '');
      const bName = String(b.name || '');
      return aName.localeCompare(bName);
    });
  }, [searchFilteredRiders]);

  /**
   * Handle associate user modal open
   */
  const handleAssociateUser = useCallback((rider: Rider) => {
    setSelectedRider(rider);
    setIsAssociateModalOpen(true);
  }, []);

  /**
   * Handle user association submission
   */
  const handleSubmitAssociation = useCallback(async () => {
    if (!selectedRider || !selectedUserId || !selectedRole) {
      setAssociationError('Please select a user and role');
      return;
    }

    setIsSubmitting(true);
    setAssociationError(null);

    try {
      // Parse existing associations
      let associations: Array<{ user_id: string; role: string }> = [];
      if (selectedRider.associatedUsers) {
        try {
          associations = JSON.parse(selectedRider.associatedUsers);
        } catch (e) {
          throw new Error('Failed to parse existing associations');
        }
      }

      // Check if association already exists
      const existingAssociation = associations.find(
        assoc => assoc.user_id === selectedUserId
      );
      
      if (existingAssociation) {
        throw new Error(`User is already associated with this rider as ${existingAssociation.role}`);
      }

      // Add new association
      associations.push({
        user_id: selectedUserId,
        role: selectedRole,
      });

      // Update rider with new associations
      await restClient.updateRider(selectedRider.rider_id, {
        rider_id: selectedRider.rider_id,
        associatedUsers: JSON.stringify(associations),
      });

      // Close modal immediately
      setIsAssociateModalOpen(false);
      setSelectedRider(null);
      setSelectedUserId('');
      setSelectedRole('Parent');
      setIsSubmitting(false);

      // Show success message
      const user = users.find(u => u.email === selectedUserId || u.username === selectedUserId);
      alert(`Successfully associated ${user?.name || user?.email || selectedUserId} with ${formatRiderName(selectedRider)} as ${selectedRole}`);

      // Wait a moment for DynamoDB to propagate the write
      await new Promise(resolve => setTimeout(resolve, 500));

      // Clear users cache and refresh riders list in background
      restClient.clearUsersCache();
      fetchRiders();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to associate user. Please try again.';
      setAssociationError(errorMessage);
      alert(`Error: ${errorMessage}`);
      setIsSubmitting(false);
    }
  }, [selectedRider, selectedUserId, selectedRole, users, fetchRiders]);

  /**
   * Handle remove user association
   */
  const handleRemoveAssociation = useCallback(async (rider: Rider, userId: string) => {
    if (!confirm('Are you sure you want to remove this association?')) {
      return;
    }

    setIsSubmitting(true);
    setAssociationError(null);

    try {
      // Parse and filter associations
      let associations: Array<{ user_id: string; role: string }> = [];
      if (rider.associatedUsers) {
        try {
          associations = JSON.parse(rider.associatedUsers);
        } catch (e) {
          throw new Error('Failed to parse existing associations');
        }
      }

      // Filter out the association
      const originalLength = associations.length;
      associations = associations.filter(assoc => assoc.user_id !== userId);

      if (associations.length === originalLength) {
        throw new Error('Association not found');
      }

      // Update rider
      await restClient.updateRider(rider.rider_id, {
        rider_id: rider.rider_id,
        associatedUsers: JSON.stringify(associations),
      });

      // Reset submitting state
      setIsSubmitting(false);

      // Show success message
      const user = users.find(u => u.email === userId || u.username === userId);
      alert(`Successfully removed association between ${user?.name || user?.email || userId} and ${formatRiderName(rider)}`);

      // Close modal if open
      setIsAssociateModalOpen(false);
      setSelectedRider(null);
      setSelectedUserId('');
      setSelectedRole('Parent');

      // Wait a moment for DynamoDB to propagate the write
      await new Promise(resolve => setTimeout(resolve, 500));

      // Clear users cache and refresh riders list in background
      restClient.clearUsersCache();
      fetchRiders();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove association. Please try again.';
      setAssociationError(errorMessage);
      alert(`Error: ${errorMessage}`);
      setIsSubmitting(false);
    }
  }, [users, fetchRiders]);

  /**
   * Handle season change
   * Requirements: 4.2, 4.3
   */
  const handleSeasonChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const season = e.target.value || null;
    setSelectedSeason(season);
  }, [setSelectedSeason]);

  /**
   * Handle type filter change
   */
  const handleTypeFilterChange = useCallback((type: 'all' | 'riders' | 'coaches') => {
    setFilterType(type);
  }, []);

  /**
   * Handle view rider
   * Requirements: 4.1
   */
  const handleViewRider = useCallback((id: string) => {
    navigate(`/riders/${id}`);
  }, [navigate]);

  /**
   * Handle edit rider
   * Requirements: 4.5
   */
  const handleEditRider = useCallback((id: string) => {
    navigate(`/riders/${id}/edit`);
  }, [navigate]);

  /**
   * Handle create rider
   * Requirements: 4.5
   */
  const handleCreateRider = useCallback(() => {
    navigate('/riders/create');
  }, [navigate]);

  if (loading && riders.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-primary-900">Riders</h1>
              <p className="text-primary-600 mt-1">View and manage riders and coaches</p>
            </div>
            
            {showActions && (
              <Button
                variant="primary"
                onClick={handleCreateRider}
              >
                Create Rider
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pt-3 pb-8">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search riders by name, email, phone, category, group, grade, or level..."
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <svg
              className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Filters and View Toggle */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Season Filter */}
          <div className="flex items-center gap-2">
            <label htmlFor="season-filter" className="text-sm font-medium text-gray-700">
              Season:
            </label>
            <select
              id="season-filter"
              value={selectedSeason || ''}
              onChange={handleSeasonChange}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Seasons</option>
              {availableSeasons.map(season => (
                <option key={season} value={season}>
                  {season}
                </option>
              ))}
            </select>
          </div>

          {/* Type Filter */}
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700">Type:</label>
            <div className="flex gap-1 bg-gray-100 rounded-md p-1">
              <button
                onClick={() => handleTypeFilterChange('all')}
                className={`px-3 py-1 text-sm rounded ${
                  filterType === 'all'
                    ? 'bg-white text-primary-600 font-medium shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All
              </button>
              <button
                onClick={() => handleTypeFilterChange('riders')}
                className={`px-3 py-1 text-sm rounded ${
                  filterType === 'riders'
                    ? 'bg-white text-primary-600 font-medium shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Riders
              </button>
              <button
                onClick={() => handleTypeFilterChange('coaches')}
                className={`px-3 py-1 text-sm rounded ${
                  filterType === 'coaches'
                    ? 'bg-white text-primary-600 font-medium shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Coaches
              </button>
            </div>
          </div>
          
          {loading && (
            <LoadingSpinner size="sm" />
          )}
        </div>

        {/* View Mode Toggle */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded ${
              viewMode === 'grid'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            aria-label="Grid view"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2 rounded ${
              viewMode === 'list'
                ? 'bg-primary-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            aria-label="List view"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Riders Grid/List */}
      {sortedRiders.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No riders found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {selectedSeason
                ? `No riders for season ${selectedSeason}`
                : 'Get started by creating a new rider'}
            </p>
            {showActions && (
              <div className="mt-6">
                <Button variant="primary" onClick={handleCreateRider}>
                  Create Rider
                </Button>
              </div>
            )}
          </div>
        </Card>
      ) : (
        <div
          className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
              : 'space-y-4'
          }
        >
          {sortedRiders.map(rider => (
            <RiderCard
              key={rider.rider_id}
              rider={rider}
              onView={handleViewRider}
              onEdit={showActions ? handleEditRider : undefined}
              onAssociateUser={showActions ? handleAssociateUser : undefined}
              onSkills={showActions && rider.type !== 'Coach' ? setSkillsRider : undefined}
              showActions={showActions}
              users={users}
              raceEvents={raceEvents}
              currentSeason={currentSeason}
            />
          ))}
        </div>
      )}
      
      {/* Associate User Modal */}
      <Modal
        isOpen={isAssociateModalOpen}
        onClose={() => {
          setIsAssociateModalOpen(false);
          setSelectedRider(null);
          setSelectedUserId('');
          setSelectedRole('Parent');
          setAssociationError(null);
        }}
        title="Associate User"
      >
        <div className="space-y-4">
          {selectedRider && (
            <>
              <div>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Rider:</span> {formatRiderName(selectedRider)}
                </p>
              </div>

              {/* Current Associations */}
              {selectedRider.associatedUsers && parseAssociatedUsers(selectedRider.associatedUsers).length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Current Associations:</h4>
                  <div className="space-y-2">
                    {parseAssociatedUsers(selectedRider.associatedUsers).map((assoc, index) => {
                      const resolved = resolveAssociation(assoc, users);
                      return (
                        <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded" data-testid="association-row">
                          <div className="text-sm">
                            <span className={resolved.resolved ? 'font-medium' : 'italic text-gray-400'}>
                              {resolved.label}
                            </span>
                            <span className="text-gray-500"> - {resolved.role}</span>
                          </div>
                          <button
                            onClick={() => handleRemoveAssociation(selectedRider, assoc.user_id)}
                            className="text-red-600 hover:text-red-800"
                            title="Remove"
                            disabled={isSubmitting}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Add New Association */}
              <div className="border-t pt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Add New Association:</h4>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="user-select" className="block text-sm font-medium text-gray-700 mb-1">
                      Select User
                    </label>
                    <select
                      id="user-select"
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      disabled={isSubmitting}
                    >
                      <option value="">-- Select User --</option>
                      {users.map(user => (
                        <option key={user.username} value={user.email}>
                          {user.name || user.given_name} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="role-select" className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      id="role-select"
                      value={selectedRole}
                      onChange={(e) => setSelectedRole(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      disabled={isSubmitting}
                    >
                      <option value="Parent">Parent</option>
                      <option value="Guardian">Guardian</option>
                      <option value="Spouse">Spouse</option>
                      <option value="Sibling">Sibling</option>
                      <option value="Self">Self</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {associationError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {associationError}
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  variant="primary"
                  onClick={handleSubmitAssociation}
                  disabled={isSubmitting || !selectedUserId}
                  className="flex-1"
                >
                  {isSubmitting ? 'Associating...' : 'Associate'}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsAssociateModalOpen(false);
                    setSelectedRider(null);
                    setSelectedUserId('');
                    setSelectedRole('Parent');
                    setAssociationError(null);
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {skillsRider && (
        <SkillsPanel
          rider={skillsRider}
          onClose={() => setSkillsRider(null)}
        />
      )}
      </div>
    </div>
  );
}

export default Riders;
